/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { User } from '../../../model/User';
import { Session } from '../../../model/Session';

class AuthenticationService {
    /** @ngInject */
    constructor($http, $q, $log, configuration, localStorageService) {
        this.$http = $http;
        this.$q = $q;
        this.$log = $log;
        this.configuration = configuration;
        this.localStorageService = localStorageService;
        this.authenticatedUser = null;
        this.$log.debug('Service \'Authentication\' loaded');
    }

    saveSession(user) {
        this.user = user;
        this.localStorageService.set(this.configuration.AUTH_STORAGE_KEY,
            angular.toJson(new Session(this.user)));
    }

    /**
     * Tries to reload an existing session from the local storage.
     */
    loadSession() {
        const jsonSession = this.localStorageService.get(this.configuration.AUTH_STORAGE_KEY);
        if (jsonSession) {
            const session = angular.fromJson(jsonSession);
            this.user = session.user;
        }
    }

    /**
     * Clears the current session, if any, front-side. Since the server uses a stateless approach, any subsequent HTTP
     * requests shall lead to a HTTP status code 401 until the user authenticates again.
     */
    clearSession() {
        this.user = null;
        this.localStorageService.remove(this.configuration.AUTH_STORAGE_KEY);
        delete this.$http.defaults.headers.common['Authorization'];
    }

    /**
     * Gets the authenticated user.
     *
     * @return {Object} The user, null if the user is not authenticated.
     */
    getAuthenticatedUser() {
        return this.user;
    }

    /**
     * Tells whether the user is authenticated.
     *
     * @return {Boolean} True if the user is authenticated.
     */
    isUserAuthenticated() {
        return this.user !== null;
    }

    /**
     * Tells whether the user is authenticated and has one of the given roles.
     *
     * @param {String[]} Array of roles.
     * @return {Boolean} True if the user is authenticated and has one of the given roles.
     */
    userHasRole(roles) {
        let hasRole = false;
        if (this.user) {
            for (const role of roles) {
                if (this.user.authorities.includes(`ROLE_${role}`)) {
                    hasRole = true;
                    break;
                }
            }
        }
        return hasRole;
    }

    /**
     * Attempts to login the user with the provided credentials. This method broadcasts an 'authentication' event if the
     * server authenticates successfully the user.
     *
     * @param {Credentials} credentials User credentials.
     * @return {Promise} The promise when the request has been sent.
     */
    login(credentials) {
        // set the basic authentication header that will be parsed in the next request and used to authenticate.
        return this.$http({
            method: 'GET',
            url: this.configuration.LOGIN_PATH,
            headers: {
                'Authorization': 'Basic ' + btoa(credentials.username + ':' + credentials.password)
            }
        }).then(response => {
            this.saveSession(new User(response.data));
        });
    }

    /**
     * Logs out the user server-side. Whatever the result, the authentication token will be cleared front-side.
     *
     * @return {Boolean} True if the user was previously logged in.
     */
    logout() {
        return this.$http.get(this.configuration.LOGOUT_PATH).then(() => true).catch(response => {
            let result;
            if (response.status === 401) {
                result = this.$q.resolve(false);
            } else {
                result = this.$q.reject(response);
            }
            return result;
        }).finally(() => {
            this.clearSession();
        });
    }
}

export { AuthenticationService };



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/security/service/AuthenticationService.js